import React, { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { Button, MenuItem, Menu } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TranslateIcon from '@material-ui/icons/Translate'
import { Check, ChevronDown, Globe } from 'lucide-react'
import Flex from 'components/Flex'
import { useDispatch, useSelector } from 'react-redux'
import { updateUser } from 'views/Home/HomeActions'
import { selectUser } from 'views/App/AppSelectors'

const FLAGS = {
  vi: '/image/vn.png',
  en: '/image/usa.png',
  kh: '/image/cam.png',
}

const useStyles = makeStyles(() => ({
  pillWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: 999,
    background: '#FFFFFF',
    color: '#111827',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    cursor: 'pointer',
    transition: 'color 0.12s, border-color 0.12s',
    '&:hover': {
      borderColor: '#22C55E',
      color: '#16A34A',
    },
  },
  pillOpen: {
    borderColor: '#22C55E',
    color: '#16A34A',
  },
  flag: {
    width: 20,
    height: 14,
    flexShrink: 0,
    objectFit: 'cover',
    borderRadius: 3,
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
  },
  chevron: {
    color: '#9CA3AF',
    flexShrink: 0,
    transition: 'transform 0.12s',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  popover: {
    position: 'absolute',
    right: 0,
    bottom: '100%',
    zIndex: 120,
    width: 212,
    marginBottom: 8,
    padding: 6,
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    background: '#FFFFFF',
    boxShadow: '0 8px 24px rgba(17, 24, 39, 0.12)',
  },
  popoverTitle: {
    padding: '4px 10px',
    color: '#9CA3AF',
    fontSize: 10.5,
    fontWeight: 600,
    lineHeight: 1.4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '8px 10px',
    border: 0,
    borderRadius: 8,
    background: 'transparent',
    color: '#111827',
    fontFamily: 'inherit',
    fontSize: 13,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color 0.12s, color 0.12s',
    '&:hover': {
      background: '#F3F4F6',
    },
  },
  optionSelected: {
    background: '#F0FDF4',
    color: '#16A34A',
    fontWeight: 600,
    '&:hover': {
      background: '#F0FDF4',
    },
  },
  optionName: {
    flex: 1,
  },
  check: {
    flexShrink: 0,
    color: '#16A34A',
  },
}))

const ChangeLanguage = ({ isUser, variant }) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [pillOpen, setPillOpen] = React.useState(false)
  const pillRef = useRef(null)
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector(selectUser)

  const handleChangeLanguage = (lng) => {
    handleClose()
    i18n.changeLanguage(lng)
    if (isUser) {
      updateLngUser(lng)
    }
  }
  const currentLanguage = useMemo(() => {
    if (!i18n?.language) return 'Change language'
    switch (i18n.language?.substring(0, 2)) {
      case 'kh':
        return 'ភាសាខ្មែរ'
      case 'vi':
        return 'Tiếng Việt'
      case 'en':
        return 'English'
      default:
        return 'Change language'
    }
  }, [i18n.language])

  const currentLangCode = i18n.language?.substring(0, 2) || 'vi'
  const currentFlag = FLAGS[currentLangCode] || FLAGS.vi
  const languageLabel = t('footer.language')

  useEffect(() => {
    if (!pillOpen) return undefined

    const handleClickOutside = (event) => {
      if (pillRef.current && !pillRef.current.contains(event.target)) {
        setPillOpen(false)
      }
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') setPillOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [pillOpen])

  const updateLngUser = (lng) => {
    dispatch(
      updateUser(
        {
          ...user,
          lang: lng,
        },
        user.id
      )
    )
  }

  const onSelectLanguage = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setPillOpen(false)
  }

  const languages = [
    {
      label: 'Tiếng Việt',
      value: 'vi',
    },
    {
      label: 'English',
      value: 'en',
    },
  ]

  return (
    <>
      {variant === "pill" ? (
        <div className={classes.pillWrap} ref={pillRef}>
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={pillOpen}
            aria-label={languageLabel}
            className={`${classes.pill} ${pillOpen ? classes.pillOpen : ""}`}
            onClick={() => setPillOpen((open) => !open)}
          >
            <Globe size={13} />
            <img className={classes.flag} src={currentFlag} alt="" aria-hidden="true" />
            {currentLanguage}
            <ChevronDown
              size={13}
              className={`${classes.chevron} ${pillOpen ? classes.chevronOpen : ""}`}
            />
          </button>

          {pillOpen && (
            <div className={classes.popover} role="listbox" aria-label={languageLabel}>
              <div className={classes.popoverTitle}>{languageLabel}</div>
              {languages.map((lng) => {
                const selected = lng.value === currentLangCode
                return (
                  <button
                    key={lng.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    autoFocus={selected}
                    className={`${classes.option} ${selected ? classes.optionSelected : ""}`}
                    onClick={() => handleChangeLanguage(lng.value)}
                  >
                    <img
                      className={classes.flag}
                      src={FLAGS[lng.value]}
                      alt=""
                      aria-hidden="true"
                    />
                    <span className={classes.optionName}>{lng.label}</span>
                    {selected && <Check size={14} className={classes.check} />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <Button className="language_select" onClick={onSelectLanguage}>
          <Flex justify="center" align="center" style={{ height: "100%" }}>
            <TranslateIcon className="mr-2" />
            {currentLanguage === "Tiếng Việt" && (
              <img className="mr-1" src="/image/vn.png" alt="icon" />
            )}
            {currentLanguage === "English" && (
              <img className="mr-1" src="/image/usa.png" alt="icon" />
            )}
            {currentLanguage === "ភាសាខ្មែរ" && (
              <img className="mr-1" src="/image/cam.png" alt="icon" />
            )}
            {currentLanguage}
          </Flex>
        </Button>
      )}
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        disableScrollLock={true}
        // keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom", 
          horizontal: "center" 
        }}
        transformOrigin={{
          vertical: "top", 
          horizontal: "center" 
        }}
      >
        {languages.map((lng, index) => (
          <MenuItem
            key={index}
            style={{
              padding: "11px 18px",
              display: "flex",
              alignItems: "center"
            }}
            onClick={() => handleChangeLanguage(lng.value)}
          >
            {lng.label === "Tiếng Việt" && (
              <img className="mr-3" src="/image/vn.png" alt="icon" />
            )}
            {lng.label === "English" && (
              <img className="mr-3" src="/image/usa.png" alt="icon" />
            )}
            {lng.label === "ភាសាខ្មែរ" && (
              <img className="mr-3" src="/image/cam.png" alt="icon" />
            )}
            {lng.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default ChangeLanguage
